import { LicenseDetails } from "@/components/license-details";
import { RegistrationDetailsSheetQuerySkeletons } from "@/components/license-details/registration-details-section-loading";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ErrorComponent from "@/components/ui/error";
import { UserType } from "@/constants";
import {
  getLicenseTypeQuestionsOptions,
  getNonRegistrantLicenseCommentsOptions,
  getNonRegistrantLicensePaymentsOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  LicenseCommentsResponseSchema,
  LicensePaymentsResponseSchema,
} from "@/hera-api/client/types.gen";
import { useLicenseDetailsWithRuntimeRefresh } from "@/hooks/use-license-details-with-runtime-refresh";
import { getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";

const EMPTY_COMMENTS: LicenseCommentsResponseSchema = { comments: [] };
const EMPTY_PAYMENTS: LicensePaymentsResponseSchema = { payments: [] };

export function ForeclosureLicenseDetailsDialog({
  licenseId,
  portal,
  onOpenChange,
}: {
  licenseId: string | null;
  portal: "government" | "operator";
  onOpenChange: (open: boolean) => void;
}) {
  const open = licenseId != null;
  const userType =
    portal === "operator" ? UserType.OPERATOR : UserType.GOVERNMENT;
  const { licenseData, isPreviewLoading, isRuntimeRefreshing, previewError } =
    useLicenseDetailsWithRuntimeRefresh({
      licenseId,
      enabled: open,
    });

  const commentsQuery = useQuery({
    ...getNonRegistrantLicenseCommentsOptions({
      path: { license_id: licenseId ?? "" },
    }),
    enabled: open,
  });
  const paymentsQuery = useQuery({
    ...getNonRegistrantLicensePaymentsOptions({
      path: { license_id: licenseId ?? "" },
    }),
    enabled: open,
  });
  const questionsQuery = useQuery({
    ...getLicenseTypeQuestionsOptions({
      path: {
        license_type_id: licenseData?.license_type.id ?? "",
        jurisdiction_id: licenseData?.jurisdiction.id ?? "",
      },
    }),
    enabled: open && Boolean(licenseData),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        elevated
        className="flex h-[min(94vh,1000px)] w-[min(96vw,1400px)] max-w-[min(96vw,1400px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(96vw,1400px)]"
      >
        <DialogHeader className="shrink-0 border-b px-6 py-4 pr-12">
          <DialogTitle>Registration details</DialogTitle>
          <DialogDescription>
            Review this registration without leaving the foreclosure case.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {previewError ? (
            <ErrorComponent
              title={getQueryErrorHeading(previewError)}
              message={getQueryErrorMessage(previewError)}
            />
          ) : !licenseData && isPreviewLoading ? (
            <RegistrationDetailsSheetQuerySkeletons />
          ) : licenseData ? (
            <LicenseDetails
              userType={userType}
              data={licenseData}
              additionalQuestions={questionsQuery.data}
              commentsData={commentsQuery.data ?? EMPTY_COMMENTS}
              paymentsData={paymentsQuery.data ?? EMPTY_PAYMENTS}
              commentsLoading={commentsQuery.isLoading}
              paymentsLoading={paymentsQuery.isLoading}
              additionalQuestionsLoading={questionsQuery.isLoading}
              isLicenseRuntimeRefreshing={isRuntimeRefreshing}
              elevatedOverlays
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
